<template>
  <div
    class="comment-box"
    :class="compactClass"
    v-on-clickaway="closeTools"
    @keydown.meta.enter="saveComment"
  >
    <UserAvatar v-if="!compact" :tooltip="false" :user="currentUser" />
    <!-- textarea -->
    <div class="comment-box__field">
      <AppTextArea
        @keydown.meta.75="openEmbedTool"
        autoHeight
        v-model="comment"
        :placeholder="$t('forms.placeholders.comment')"
        @focus="openTools"
        @input="updateValue"
      />
      <transition name="expand-bottom">
        <div v-if="showTools || compact" class="comment-box__footer">
          <div class="comment-box__tools">
            <!-- CONTROLS -->
            <div class="comment-box__controls">
              <!-- save -->
              <AppButton :disabled="!comment" @click="comment && saveComment()">
                {{ $t('ui.buttons.save') }}
              </AppButton>
              <!-- cancel -->
              <AppButtonIcon v-if="compact" @click="closeTools" />
            </div>
            <!-- OPTIONS -->
            <div class="comment-box__options">
              <!-- embed link -->
              <CommentBoxLinkEmbed @embeded-link="setEmbedLink" />
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
// Utilities
import { EventBus } from '@/main'
import { missClicked } from 'Utils'
import { mixin as clickaway } from 'vue-clickaway'
// Components
import AppButton from 'Ui/AppButton'
import AppTextArea from 'Ui/AppTextArea'
import AppButtonIcon from 'Ui/AppButtonIcon'
import UserAvatar from 'Components/UserAvatar'

export default {
  name: 'CommentBox',

  components: {
    AppButton,
    UserAvatar,
    AppTextArea,
    AppButtonIcon,
    CommentBoxLinkEmbed: () => import('./components/CommentBoxLinkEmbed'),
  },

  props: {
    value: {
      type: String,
      default: '',
    },

    compact: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [clickaway],

  data() {
    return {
      showTools: false,
      comment: this.value,
      textArea: null,
    }
  },

  methods: {
    openTools() {
      this.showTools = true
    },

    closeTools(event) {
      // if focus within popper or outside clicked while selecting
      if (event?.path?.some((p) => p.classList?.contains('tippy-popper')) || missClicked()) return
      this.$emit('blur')
      this.textArea.blur()
      this.showTools = false
    },

    openEmbedTool() {
      EventBus.$emit('open-box')
    },

    setEmbedLink(embed) {
      if (this.comment.indexOf(embed.name) != -1) {
        this.comment = this.comment.replace(embed.name, embed.embed)
      } else {
        this.comment += ` ${embed.embed}`
      }
      this.textArea.focus()
    },

    saveComment() {
      if (!this.comment) return
      this.$emit('save-comment', this.comment)

      if (this.compact) return
      this.comment = ''
      this.closeTools()
    },

    updateValue(value) {
      this.$emit('input', value)
    },
  },

  computed: {
    currentUser() {
      return this.$store.state.user.currentUser
    },

    compactClass() {
      return this.compact && 'compact'
    },
  },

  mounted() {
    this.textArea = this.$el.querySelector('.app-textarea')
  },
}
</script>

<style scoped>
.comment-box {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 20px;
}

.comment-box.compact {
  grid-template-columns: 1fr;
  margin-bottom: 0;
}

.comment-box__tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  padding: 5px 20px 10px;
}

.comment-box__field {
  border: 1px solid var(--c-secondary-500);
  border-radius: 4px;
  box-shadow: 0 1px 2px -1px var(--rgba--light-blue);
  background-color: #fff;
  transition: var(--transition--default);
}

.comment-box__field:focus-within {
  border-color: var(--c-secondary);
  box-shadow: 0 1px 8px -3px var(--rgba--light-blue);
}

.comment-box__field >>> .app-textarea {
  line-height: 22px;
  padding: 15px 20px;
  border: none;
  box-shadow: none;
}

.comment-box__controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.comment-box__controls .app-button--icon {
  font-size: 20px;
  min-width: 34px;
  border-radius: 4px;
  background-color: transparent;
}

.comment-box__controls .app-button--icon:hover {
  background-color: var(--c-secondary-400);
}

.comment-box__controls .app-button {
  font-size: 12px;
  min-height: 32px;
}

.comment-box__controls .app-button:not(:disabled):hover {
  filter: brightness(1.1);
}

/* Responsive --------- */
@media (max-width: 680px) {
  .comment-box__tools {
    padding: 10px;
  }
}
</style>
